import { useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type {
  PositionBudget,
  PositionBudgetSummary,
  RosterSettings,
  ScholarshipModel,
  ScholarshipPlayer,
  ScholarshipPlayerPosition,
} from '../../../../types/scholarships';
import { DepthChartCardRow } from './DepthChartCardRow';
import { DIAMOND_VIEWBOX } from './depthChartPositions';
import type { CardAppearance, DepthChartCardSpec } from './depthChartPositions';
import { formatGroupToggleAggregate, type DepthChartDisplayMode } from './scholarshipPercent';
import { PositionBudgetLine } from './PositionBudgetLine';
import { NeedLine } from './NeedLine';

/** Rows a card shows before "Show all (N)" where no slot-derived cap applies (list, unfitted field). */
export const DEPTH_CHART_VISIBLE_ROWS = 6;

const toPercentX = (units: number) => `${(units / DIAMOND_VIEWBOX.width) * 100}%`;
const toPercentY = (units: number) => `${(units / DIAMOND_VIEWBOX.height) * 100}%`;

/**
 * Where an opened ("Show all") card sits on the field. It must stay inside the 1400x800 frame:
 * a card that grew past the frame's bottom made the chart's scroll box scroll vertically, and on a
 * classic-scrollbar platform that narrowed the measured width and rescaled the whole field.
 *
 * A card in the lower half grows UPWARD, its bottom pinned to the bottom of its slot, with room up
 * to the frame's top; an upper card grows downward with room down to the frame's bottom. Anything
 * deeper than that scrolls inside the card (depthChart.css).
 */
const liftedStyle = (spec: DepthChartCardSpec): React.CSSProperties => {
  const frameHeight = DIAMOND_VIEWBOX.height;
  if (spec.anchor.y >= frameHeight / 2) {
    const slotBottom = Math.min(frameHeight, spec.anchor.y + spec.size.maxHeight);
    return {
      top: 'auto',
      bottom: toPercentY(frameHeight - slotBottom),
      maxHeight: toPercentY(slotBottom),
    };
  }
  return {
    top: toPercentY(spec.anchor.y),
    maxHeight: toPercentY(frameHeight - spec.anchor.y),
  };
};

interface Props {
  spec: DepthChartCardSpec;
  /** Sport-resolved heading -- 'DP' rather than 'DH' on a softball board. */
  label: string;
  appearances: CardAppearance[];
  scholarshipModel: ScholarshipModel;
  displayMode: DepthChartDisplayMode;
  positionBudget?: PositionBudgetSummary;
  /** The configured need for this position: players wanted and a note. */
  need?: PositionBudget;
  settings: RosterSettings;
  onClickPlayer: (player: ScholarshipPlayer) => void;
  /** Omitted: the card is read-only (no drag handles). */
  onReorder?: (position: ScholarshipPlayerPosition, orderedIds: string[]) => void;
  /** The chart's zoom, so a dragged row follows the pointer rather than zoom times the pointer. */
  zoom?: number;
  /** This card's first handle carries the `rosters.depth.drag` tour anchor. */
  ownsDragAnchor?: boolean;
  /** False in the list layout: the card sits in a grid, not at its place on the field. */
  positioned?: boolean;
  /** Rows shown before "Show all". The fitted field passes what the card's slot holds. */
  rowCap?: number;
}

export const DepthChartCard = ({
  spec,
  label,
  appearances,
  scholarshipModel,
  displayMode,
  positionBudget,
  need,
  settings,
  onClickPlayer,
  onReorder,
  zoom = 1,
  ownsDragAnchor = false,
  positioned = true,
  rowCap = DEPTH_CHART_VISIBLE_ROWS,
}: Props) => {
  const [expanded, setExpanded] = useState(false);

  // Count + aggregate use PRIMARY appearances only — secondary entries are visual-only
  // (money counts once, on the player's primary card).
  const primaryPlayers = useMemo(
    () => appearances.filter((a) => a.appearance === 'primary').map((a) => a.player),
    [appearances],
  );
  const avg = useMemo(
    () => formatGroupToggleAggregate(primaryPlayers, scholarshipModel, displayMode),
    [primaryPlayers, scholarshipModel, displayMode],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /*
    The diamond is scaled with a CSS transform, and dnd-kit moves the dragged row in unscaled
    pixels, so at 150% a row ran ahead of the pointer by half again. Dividing by the zoom keeps it
    under the cursor. Locked to the vertical axis, since a card is a single column.
  */
  const followPointer: Modifier = ({ transform }) => ({
    ...transform,
    x: 0,
    y: transform.y / (zoom || 1),
  });

  const collapsible = appearances.length > rowCap;
  const visible = collapsible && !expanded
    ? appearances.slice(0, rowCap)
    : appearances;
  // Opened on the field, a card grows past its slot and sits over its neighbours until closed
  // (see liftedStyle for how it stays inside the frame).
  const liftedOnField = positioned && collapsible && expanded;
  const visiblePrimaryIds = visible
    .filter((a) => a.appearance === 'primary')
    .map((a) => a.player.id);
  const canDrag = onReorder != null && primaryPlayers.length > 1;

  const handleDragEnd = (event: DragEndEvent) => {
    if (!onReorder) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    // Against the FULL primary list, so a collapsed card still sends every player at this position.
    const oldIndex = primaryPlayers.findIndex((p) => p.id === active.id);
    const newIndex = primaryPlayers.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(
      spec.reorderPosition,
      arrayMove(primaryPlayers, oldIndex, newIndex).map((p) => p.id),
    );
  };

  // Percentages of the 1400x800 card space, not of the field art. DIAMOND_VIEWBOX is the one
  // source for those divisors -- they were literals here until the space widened to make room
  // for the flanking cards, and a stale divisor silently misplaces every card at once.
  const style: React.CSSProperties | undefined = positioned ? {
    left: toPercentX(spec.anchor.x),
    width: toPercentX(spec.size.width),
    minHeight: toPercentY(spec.size.height),
    ...(liftedOnField
      ? liftedStyle(spec)
      : {
          top: toPercentY(spec.anchor.y),
          // The ceiling that stops a deep position growing down through the card beneath it.
          // The rows past it are behind "Show all" (on the fitted field, the row cap is what the
          // slot holds); the unfitted fallback's list scrolls instead. See depthChart.css.
          maxHeight: toPercentY(spec.size.maxHeight),
        }),
  } : undefined;

  const listId = `dc-${spec.id}-list`;
  // Primaries lead the card, so the first visible primary is the first row with a handle.
  const dragAnchorId = ownsDragAnchor && canDrag ? visiblePrimaryIds[0] : undefined;

  const list = (
    <ol className="depth-chart-card__list" id={listId}>
      {visible.map((a, idx) => {
        const draggable = canDrag && a.appearance === 'primary';
        return (
          <DepthChartCardRow
            key={`${a.player.id}-${a.appearance}`}
            player={a.player}
            appearance={a.appearance}
            depthIndex={idx}
            scholarshipModel={scholarshipModel}
            settings={settings}
            draggable={draggable}
            showHandleSpace={canDrag}
            dragAnchor={draggable && a.player.id === dragAnchorId}
            onClick={() => onClickPlayer(a.player)}
          />
        );
      })}
    </ol>
  );

  return (
    <section
      className={`depth-chart-card${liftedOnField ? ' depth-chart-card--lifted' : ''}`}
      style={style}
      aria-labelledby={`dc-${spec.id}-heading`}
    >
      <header className="depth-chart-card__header">
        <h3 id={`dc-${spec.id}-heading`} className="depth-chart-card__label">
          {label}
        </h3>
        <span className="depth-chart-card__count">({primaryPlayers.length})</span>
        <span
          className={`depth-chart-card__avg${avg.isWalkOn ? ' depth-chart-card__avg--walkon' : ''}`}
          title={avg.isWalkOn ? 'Group average — all players are walk-ons or have $0 awarded' : undefined}
        >
          {avg.label} {avg.text}
        </span>
      </header>

      <NeedLine need={need} />

      {positionBudget && (
        <PositionBudgetLine budget={positionBudget} scholarshipModel={scholarshipModel} />
      )}

      {appearances.length === 0 ? (
        <div className="depth-chart-card__empty">No players</div>
      ) : canDrag ? (
        <DndContext
          id={`depth-chart-${spec.id}`}
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[followPointer]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={visiblePrimaryIds} strategy={verticalListSortingStrategy}>
            {list}
          </SortableContext>
        </DndContext>
      ) : (
        list
      )}

      {collapsible && (
        <button
          type="button"
          className="depth-chart-card__show-all"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show fewer' : `Show all (${appearances.length})`}
        </button>
      )}

      {spec.sharedPool && (
        <footer className="depth-chart-card__shared-pool-note">
          Shared with LF, CF, RF
        </footer>
      )}
    </section>
  );
};
